/* eslint-disable react/forbid-prop-types */
/* eslint-disable import/no-unresolved */
import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Paper,
  Typography,
  Button,
  CircularProgress,
  IconButton,
  Tooltip,
} from "@mui/material";
import MyLocationIcon from "@mui/icons-material/MyLocation";
import CloseIcon from "@mui/icons-material/Close";
import GoogleMap from "components/GoogleMap";
import Marker from "components/Marker";
import "./styles.scss";

const MapMarker = ({ text }) => (
  <div className="map-marker">
    <div className="marker-pin" />
    <div className="marker-pulse" />
  </div>
);

const MapAddressPicker = ({
  onLocationSelect,
  onClose,
  initialLocation,
  isLoading,
}) => {
  const [center, setCenter] = useState(
    initialLocation || { lat: 28.6139, lng: 77.209 } // Default to Delhi
  );
  const [markerPosition, setMarkerPosition] = useState(center);
  const [mapInstance, setMapInstance] = useState(null);
  const [mapsInstance, setMapsInstance] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isGettingLocation, setIsGettingLocation] = useState(false);
  const [locationError, setLocationError] = useState(null);
  const mapRef = useRef(null);

  // Get current location on component mount
  useEffect(() => {
    if (initialLocation) {
      setCenter(initialLocation);
      setMarkerPosition(initialLocation);
    } else {
      // Automatically get current location when opening
      getCurrentLocation();
    }
  }, []);

  const getCurrentLocation = () => {
    if (navigator.geolocation) {
      setIsGettingLocation(true);
      setLocationError(null);
      
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const newLocation = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          };
          setCenter(newLocation);
          setMarkerPosition(newLocation);
          setIsGettingLocation(false);
          
          // Pan to location after a short delay to ensure map is loaded
          setTimeout(() => {
            if (mapInstance) {
              mapInstance.panTo(new window.google.maps.LatLng(newLocation.lat, newLocation.lng));
              mapInstance.setZoom(15);
            }
          }, 100);
        },
        (error) => {
          console.error("Error getting current location:", error);
          setIsGettingLocation(false);
          
          let errorMessage = "Unable to get your location. ";
          switch(error.code) {
            case error.PERMISSION_DENIED:
              errorMessage += "Please enable location permissions.";
              break;
            case error.POSITION_UNAVAILABLE:
              errorMessage += "Location information unavailable.";
              break;
            case error.TIMEOUT:
              errorMessage += "Location request timed out.";
              break;
            default:
              errorMessage += "An unknown error occurred.";
          }
          setLocationError(errorMessage);
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0,
        }
      );
    } else {
      setLocationError("Geolocation is not supported by this browser.");
    }
  };

  const handleApiLoaded = ({ map, maps }) => {
    setMapInstance(map);
    setMapsInstance(maps);
  };

  const handleMapClick = ({ lat, lng }) => {
    if (!isDragging) {
      setMarkerPosition({ lat, lng });
    }
  };

  const handleDragEnd = () => {
    setIsDragging(false);
  };

  const handleCurrentLocation = () => {
    getCurrentLocation();
  };

  const handleConfirmLocation = () => {
    onLocationSelect(markerPosition);
  };

  return (
    <Paper className="map-address-picker" elevation={3}>
      <Box className="map-header">
        <Typography variant="h6" className="map-title">
          Select Delivery Location
        </Typography>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Box>

      <Box className="map-instructions">
        {isGettingLocation ? (
          <Box display="flex" alignItems="center" gap={1}>
            <CircularProgress size={16} />
            <Typography variant="body2" color="textSecondary">
              Getting your current location...
            </Typography>
          </Box>
        ) : locationError ? (
          <Typography variant="body2" color="error">
            {locationError} - Please select location manually or enable location services.
          </Typography>
        ) : (
          <Typography variant="body2" color="textSecondary">
            Drag the pin or click on the map to select your exact location
          </Typography>
        )}
      </Box>

      <Box className="map-container">
        <div style={{ height: "500px", width: "100%", position: "relative" }}>
          {isGettingLocation ? (
            <Box 
              display="flex" 
              justifyContent="center" 
              alignItems="center" 
              height="100%" 
              bgcolor="#f5f5f5"
            >
              <Box textAlign="center">
                <CircularProgress size={48} />
                <Typography variant="body2" color="textSecondary" mt={2}>
                  Locating you...
                </Typography>
              </Box>
            </Box>
          ) : (
            <>
              <GoogleMap
                center={{ lat: center.lat, lng: center.lng }}
                zoom={15}
                style={{ height: "100%", width: "100%" }}
                yesIWantToUseGoogleMapApiInternals
                onGoogleApiLoaded={handleApiLoaded}
                onClick={handleMapClick}
                onDrag={() => setIsDragging(true)}
                onDragEnd={handleDragEnd}
                options={{
                  fullscreenControl: false,
                  zoomControl: true,
                  streetViewControl: false,
                  mapTypeControl: false,
                }}
              >
                <Marker
                  lat={markerPosition.lat}
                  lng={markerPosition.lng}
                  text="Your Location"
                />
              </GoogleMap>

              <Tooltip title="Refresh current location" placement="left">
                <IconButton
                  className="current-location-btn"
                  color="primary"
                  onClick={handleCurrentLocation}
                  size="large"
                  disabled={isGettingLocation}
                >
                  <MyLocationIcon />
                </IconButton>
              </Tooltip>
            </>
          )}
        </div>
      </Box>

      <Box className="map-actions">
        <Button
          variant="outlined"
          onClick={onClose}
          disabled={isLoading || isGettingLocation}
          sx={{ mr: 2 }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleConfirmLocation}
          disabled={isLoading || isGettingLocation}
          startIcon={isLoading && <CircularProgress size={20} />}
        >
          {isLoading ? "Loading Address..." : "Confirm Location"}
        </Button>
      </Box>

      <Box className="map-coordinates">
        <Typography variant="caption" color="textSecondary">
          Selected Location: {markerPosition.lat.toFixed(6)},{" "}
          {markerPosition.lng.toFixed(6)}
        </Typography>
      </Box>
    </Paper>
  );
};

MapAddressPicker.propTypes = {
  onLocationSelect: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  initialLocation: PropTypes.shape({
    lat: PropTypes.number,
    lng: PropTypes.number,
  }),
  isLoading: PropTypes.bool,
};

MapAddressPicker.defaultProps = {
  initialLocation: null,
  isLoading: false,
};

export default MapAddressPicker;

